import dayjs, { Dayjs } from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { Weekdays } from './constants'

dayjs.extend(relativeTime)

export const formatDate = (date: string | null | undefined): string | null => {
  return date ? dayjs(date).format('L') : null
}

export function formatRelativeUtcTimestamp(timestamp: string): string {
  return dayjs.utc(timestamp).local().fromNow()
}

export const formatUTCDate = (
  date: string | null | undefined,
  timezone: string,
  format?: string
): string | null => {
  return date
    ? dayjs
        .utc(date)
        .tz(timezone)
        .format(format ?? 'L')
    : null
}

export const calculateAgeInYears = (birthdate: Dayjs): number => {
  const today = dayjs()
  const birthDate = dayjs(birthdate)

  const age = today.diff(birthDate, 'year')
  const nextBirthday = birthDate.add(age, 'year')

  if (today.isBefore(nextBirthday, 'day')) {
    return age - 1
  }

  return age
}

export const calculateAgeInMonths = (birthdate: Dayjs): number => {
  const today = dayjs()
  const birthDate = dayjs(birthdate)

  const ageInMonths = today.diff(birthDate, 'month')
  const nextBirthday = birthDate.add(ageInMonths, 'month')

  if (today.isBefore(nextBirthday, 'day')) {
    return ageInMonths - 1
  }

  return ageInMonths
}

export const getAgeString = (birthdate: Dayjs): string => {
  const age = calculateAgeInYears(birthdate)

  if (age > 0) {
    return `${age} year old`
  } else {
    const ageInMonths = calculateAgeInMonths(birthdate)
    return `${ageInMonths} month old`
  }
}

export const getLastPeriod = (periods: [Dayjs, Dayjs][]): [Dayjs, Dayjs] | undefined => {
  const latestPeriod = [...periods].sort((a, b) => a[1].unix() - b[1].unix()).pop()
  return latestPeriod
}

export const periodIsContained = (period: [Dayjs, Dayjs], otherPeriod: [Dayjs, Dayjs]): boolean => {
  const [periodStart, periodEnd] = period
  const [otherPeriodStart, otherPeriodEnd] = otherPeriod
  // Check intersection of the dates
  const hasIntersections =
    dayjs(periodStart).isBetween(otherPeriodStart, otherPeriodEnd) &&
    dayjs(periodEnd).isBetween(otherPeriodStart, otherPeriodEnd)

  return hasIntersections
}

export const periodIsIntersecting = (
  period: [Dayjs, Dayjs],
  otherPeriod: [Dayjs, Dayjs],
  includeEndpoints = true
): boolean => {
  const [periodStart, periodEnd] = period
  const [otherPeriodStart, otherPeriodEnd] = otherPeriod

  const endpointsMatch = includeEndpoints
    ? dayjs(periodStart).isSame(otherPeriodStart) ||
      dayjs(periodStart).isSame(otherPeriodEnd) ||
      dayjs(periodEnd).isSame(otherPeriodStart) ||
      dayjs(periodEnd).isSame(otherPeriodEnd)
    : false

  // Check intersection of the dates
  const hasIntersections =
    // If they're intersecting (between the two intervals)...
    dayjs(periodStart).isBetween(otherPeriodStart, otherPeriodEnd) ||
    dayjs(periodEnd).isBetween(otherPeriodStart, otherPeriodEnd) ||
    dayjs(otherPeriodStart).isBetween(periodStart, periodEnd) ||
    dayjs(otherPeriodEnd).isBetween(periodStart, periodEnd) ||
    // Or they're the endpoints of the interval
    endpointsMatch

  return hasIntersections
}

export const zuluToZeroOffset = (zuluDateString: string): string => {
  return zuluDateString.replace(/\.\d+Z$/, '+00:00')
}

export const TIME_FORMAT = 'h:mm A'

export const formatTime = (date: string | null | Date | undefined | Dayjs): string | null => {
  if (!date) return null

  return dayjs(date).format(TIME_FORMAT)
}

export const transformToTimezoneOrUtc = (
  date: string | Dayjs,
  timezone?: string,
  startOfDay = true
): Dayjs => {
  const isUtc = dayjs(date).isUTC()
  if (timezone) {
    return startOfDay ? dayjs(date).tz(timezone).startOf('day') : dayjs(date).tz(timezone)
  } else if (isUtc) {
    return startOfDay ? dayjs(date).startOf('day') : dayjs(date)
  } else {
    return startOfDay ? dayjs(date).utc().startOf('day') : dayjs(date).utc()
  }
}

export const isTimePeriodInEffect = (
  timezone?: string,
  start?: string | null,
  end?: string | null,
  targetDate: string = dayjs().format()
): boolean => {
  const today = transformToTimezoneOrUtc(targetDate, timezone)
  const startDate = start ? transformToTimezoneOrUtc(start, timezone) : null
  const endDate = end ? transformToTimezoneOrUtc(end, timezone) : null
  let isInTimePeriod = false
  if (startDate && endDate) {
    isInTimePeriod = today.isBetween(startDate, endDate, 'day', '[]')
  } else if (startDate) {
    isInTimePeriod = today.isSame(startDate, 'day') || today.isAfter(startDate, 'day')
  }
  return isInTimePeriod
}

type period = {
  start?: string
  end?: string | null
}

export const doPeriodsIntersect = (
  periodA?: period | null,
  periodB?: period | null,
  timezone?: string
): boolean => {
  const startDate = periodA?.start ? transformToTimezoneOrUtc(periodA?.start, timezone) : null
  const endDate = periodA?.end ? transformToTimezoneOrUtc(periodA?.end, timezone) : null
  const otherStartDate = periodB?.start ? transformToTimezoneOrUtc(periodB?.start, timezone) : null
  const otherEndDate = periodB?.end
    ? transformToTimezoneOrUtc(periodB?.end, timezone)
    : periodB?.end
  let isInTimePeriod = false
  if (startDate && endDate && otherStartDate && otherEndDate) {
    isInTimePeriod = startDate.isBetween(otherStartDate, otherEndDate, 'day', '[]')
    isInTimePeriod = isInTimePeriod && endDate.isBetween(otherStartDate, otherEndDate, 'day', '[]')
  }
  return isInTimePeriod
}

export const getWeekdayAbbreviation = (weekday: Weekdays): string => {
  switch (weekday) {
    case Weekdays.SUNDAY:
      return 'Sun'
    case Weekdays.MONDAY:
      return 'Mon'
    case Weekdays.TUESDAY:
      return 'Tue'
    case Weekdays.WEDNESDAY:
      return 'Wed'
    case Weekdays.THURSDAY:
      return 'Thu'
    case Weekdays.FRIDAY:
      return 'Fri'
    case Weekdays.SATURDAY:
      return 'Sat'
  }
}

export const WEEKDAY_TO_DAY_INDEX: Record<Weekdays, number> = {
  [Weekdays.SUNDAY]: 0,
  [Weekdays.MONDAY]: 1,
  [Weekdays.TUESDAY]: 2,
  [Weekdays.WEDNESDAY]: 3,
  [Weekdays.THURSDAY]: 4,
  [Weekdays.FRIDAY]: 5,
  [Weekdays.SATURDAY]: 6
}

/**
 * Return midnight on the next date matching the weekday in the given timezone, today included.
 * Anchoring a newly added visit to its own weekday keeps its stored date meaningful instead of
 * stamping today, which the overlap validator would otherwise read literally.
 */
export const getNextOccurrenceOfWeekday = (weekday: Weekdays, timezone: string): Dayjs => {
  const startOfToday = dayjs().tz(timezone).startOf('day')
  const targetDayIndex = WEEKDAY_TO_DAY_INDEX[weekday]
  const daysUntilTarget = (targetDayIndex - startOfToday.day() + 7) % 7
  // Re-resolve the calendar date through the timezone database so day arithmetic across a DST
  // transition does not retain the original UTC offset and land on the previous local day.
  const targetDate = startOfToday.add(daysUntilTarget, 'day').format('YYYY-MM-DD')
  return dayjs.tz(targetDate, timezone).startOf('day')
}

export function isDateLike(value: unknown): value is string | number | Date | dayjs.Dayjs {
  if (typeof value === 'string' || typeof value === 'number' || value instanceof Date) {
    return true
  }
  if (dayjs.isDayjs(value)) {
    return true
  }
  return false
}
